"use client";

import React, { useState } from "react";
import { ArrowUpRight, MapPin, TrendingUp } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
interface CaseStudyItem {
  id: string;
  title: string;
  tag: string;
  location: string;
  description: string;
  metric: string;
  category: "resorts" | "boutique" | "urban" | "eco";
}
const CASE_STUDIES: CaseStudyItem[] = [{
  id: "island-resort-maldives",
  title: "Island Resort, Maldives",
  tag: "Island Resort",
  location: "Maldives",
  description: "Cleaned up OTA channel mix, introduced dynamic villa tier pricing, and boosted direct website booking conversion.",
  metric: "Direct Booking Share +42%",
  category: "resorts"
}, {
  id: "boutique-hotel-thailand",
  title: "Boutique Hotel, Thailand",
  tag: "Boutique Hotel",
  location: "Phuket, Thailand",
  description: "Enforced rate fences, minimum stay rules (MLOS), and direct listing visibility on Booking.com and Agoda.",
  metric: "RevPAR Growth +38%",
  category: "boutique"
}, {
  id: "budget-hotel-kenya",
  title: "Budget Hotel, Kenya",
  tag: "Budget Hotel",
  location: "Nairobi, Kenya",
  description: "Synchronized live inventory across regional OTAs to stop distribution lag and prevent distressed unsold room nights.",
  metric: "OTA Conversion +48%",
  category: "urban"
}, {
  id: "business-hotel-uk",
  title: "Business Hotel, UK",
  tag: "Business Hotel",
  location: "London, United Kingdom",
  description: "Introduced corporate rate grids with pickup pace rules, raising mid-week business ADR without hurting occupancy.",
  metric: "Weekday Corporate ADR +31%",
  category: "urban"
}, {
  id: "eco-lodge-zanzibar",
  title: "Eco Lodge, Zanzibar",
  tag: "Eco Lodge",
  location: "Zanzibar, Tanzania",
  description: "Built experience-based packages and protected direct rate parity across international booking channels.",
  metric: "Low-Season Occupancy +54%",
  category: "eco"
}, {
  id: "heritage-property-tanzania",
  title: "Heritage Property, Tanzania",
  tag: "Heritage Property",
  location: "Arusha, Tanzania",
  description: "Repositioned heritage suites on luxury channels, stopped extranet undercutting, and lifted direct packaging rates.",
  metric: "Average Daily Rate (ADR) +35%",
  category: "eco"
}, {
  id: "luxury-villa-philippines",
  title: "Luxury Villa, Philippines",
  tag: "Luxury Villa",
  location: "Palawan, Philippines",
  description: "Applied strict length-of-stay rules and direct feeder market pricing to protect peak-season inventory margins.",
  metric: "Total Gross Revenue +45%",
  category: "boutique"
}, {
  id: "resort-chain-maldives",
  title: "Resort Chain, Maldives",
  tag: "Resort Chain",
  location: "Maldives Archipelago",
  description: "Centralized group yield controls across multi-property inventory clusters to protect portfolio RevPAR.",
  metric: "Portfolio RevPAR +29%",
  category: "resorts"
}, {
  id: "urban-hotel-india",
  title: "Urban Hotel, India",
  tag: "Urban Hotel",
  location: "Bengaluru, India",
  description: "Audited PMS-to-channel manager mapping, fixed rate parity leaks, and boosted corporate direct bookings.",
  metric: "Direct Corporate Share +36%",
  category: "urban"
}, {
  id: "5-star-resort-india",
  title: "5-Star Resort, India",
  tag: "5-Star Resort",
  location: "Goa, India",
  description: "Managed peak-season demand compression with daily manual yield discipline, driving premium ADR without overbooking.",
  metric: "Peak Season ADR +44%",
  category: "resorts"
}];
export default function CaseStudyDirectory() {
  const [selectedTab, setSelectedTab] = useState<string>("all");
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLElement>({
    threshold: 0.1
  });
  const filteredStudies = selectedTab === "all" ? CASE_STUDIES : CASE_STUDIES.filter(item => {
    if (selectedTab === "resorts") return item.category === "resorts";
    if (selectedTab === "boutique") return item.category === "boutique" || item.id === "5-star-resort-india";
    if (selectedTab === "urban") return item.category === "urban";
    if (selectedTab === "eco") return item.category === "eco";
    return true;
  });
  return <section id="casestudydirectory" data-nav="light" ref={ref} className="bg-background text-foreground py-20 md:py-28 relative overflow-hidden transition-opacity duration-700" style={{
    opacity: isVisible ? 1 : 0
  }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
          <Badge variant="secondary" className="mb-4 px-3 py-1 font-semibold text-xs tracking-wider uppercase bg-secondary text-secondary-foreground">
            Proven Track Record
          </Badge>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground mb-4">
            Case study directory
          </h2>
          <p className="text-muted-foreground text-base sm:text-lg leading-relaxed">
            Real turnarounds from 700+ hotels managed over 15+ years. Here is how hands-on yield management, rate parity enforcement, and OTA audit discipline protect net GOPPAR.
          </p>
        </div>

        <Tabs defaultValue="all" value={selectedTab} onValueChange={setSelectedTab} className="w-full flex flex-col items-center mb-12">
          <TabsList className="bg-muted p-1 rounded-lg flex flex-wrap justify-center gap-1.5 max-w-3xl w-full sm:w-auto">
            <TabsTrigger value="all" className="px-4 py-2 text-sm font-medium transition-all data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              All Properties
            </TabsTrigger>
            <TabsTrigger value="resorts" className="px-4 py-2 text-sm font-medium transition-all data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              Resorts &amp; Chains
            </TabsTrigger>
            <TabsTrigger value="boutique" className="px-4 py-2 text-sm font-medium transition-all data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              Boutique &amp; Luxury
            </TabsTrigger>
            <TabsTrigger value="urban" className="px-4 py-2 text-sm font-medium transition-all data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              Urban &amp; Business
            </TabsTrigger>
            <TabsTrigger value="eco" className="px-4 py-2 text-sm font-medium transition-all data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              Eco &amp; Heritage
            </TabsTrigger>
          </TabsList>
        </Tabs>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {filteredStudies.map((study, index) => <Card key={study.id} data-index={index} className="bg-card text-card-foreground border border-border/80 shadow-md hover:shadow-xl transition-all duration-300 flex flex-col justify-between group rounded-xl hover:-translate-y-1">
              <div>
                <CardHeader className="p-6 pb-4">
                  <div className="flex items-center justify-between gap-3 mb-3">
                    <span className="inline-flex items-center text-xs font-semibold px-2.5 py-1 rounded-md bg-secondary/15 text-secondary-foreground border border-secondary/30">
                      {study.tag}
                    </span>
                    <div className="flex items-center text-xs text-muted-foreground gap-1">
                      <MapPin className="size-3.5 text-secondary shrink-0" />
                      <span>{study.location}</span>
                    </div>
                  </div>
                  <CardTitle className="font-serif text-xl sm:text-2xl font-bold text-foreground group-hover:text-secondary transition-colors duration-200">
                    {study.title}
                  </CardTitle>
                </CardHeader>

                <CardContent className="px-6 py-2">
                  <p className="text-muted-foreground text-sm leading-relaxed mb-6">
                    {study.description}
                  </p>

                  <div className="p-3.5 rounded-lg bg-muted/60 border border-border/60 flex items-center gap-3">
                    <div className="size-8 rounded-md bg-secondary/20 flex items-center justify-center text-secondary-foreground shrink-0">
                      <TrendingUp className="size-4 text-secondary" />
                    </div>
                    <div>
                      <div className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                        Headline Impact
                      </div>
                      <div className="text-sm font-bold text-foreground">
                        {study.metric}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </div>

              <CardFooter className="p-6 pt-4 border-t border-border/60 mt-4">
                <Button asChild variant="outline" className="w-full justify-between font-semibold group/btn border-border hover:border-secondary hover:bg-secondary/10 text-foreground">
                  <Link to="#">
                    <span>Read Case Study</span>
                    <ArrowUpRight className="size-4 transition-transform duration-200 group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5 text-secondary" />
                  </Link>
                </Button>
              </CardFooter>
            </Card>)}
        </div>
      </div>
    </section>;
}